import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IAuthResponse } from '@g99-product-web-ui/shared-interface-lib';
import { HttpService, ServiceType } from '@g99-product-web-ui/shared-service';
import { API_ENDPOINTS } from '@g99-product-web-ui/shared-util-lib';
import { Observable } from 'rxjs';

import {
  IMFAVerificationPayload,
  IUserAuthenticationDetails,
  OtpVerificationPayload,
  SmsSetupPayload,
} from '../interface/IMfa.interface';

@Injectable({
  providedIn: 'root',
})
export class MultifactorAuthenticationService {
  constructor(private readonly _http: HttpService) {}

  /**
   * Initiates SMS-based MFA setup.
   * @param phoneNumber User's phone number for SMS verification.
   * @param accessToken Access token for API authentication.
   * @returns An observable of the HTTP POST response.
   */
  public initiateSmsSetup(phoneNumber: number, accessToken: string) {
    const url = API_ENDPOINTS.AUTH_ENDPOINTS.newSmsSetup;
    const payload: SmsSetupPayload = { phone: phoneNumber, accessToken };
    return this._http.post(
      url,
      payload,
      {
        attachTenantId: true,
      },
      ServiceType.AUTH
    );
  }

  /**
   * Verifies the SMS OTP during MFA setup.
   * @param otpCode The OTP received via SMS.
   * @param accessToken Access token for API authentication.
   * @returns An observable of the HTTP POST response.
   */
  public verifySmsOtp(otpCode: number, accessToken: string) {
    const url = API_ENDPOINTS.AUTH_ENDPOINTS.newSmsVerifySetup;
    const payload: OtpVerificationPayload = { code: otpCode, accessToken };
    return this._http.post(
      url,
      payload,
      {
        attachTenantId: true,
      },
      ServiceType.AUTH
    );
  }

  /**
   * Initiates TOTP-based MFA setup.
   * @param accessToken Access token for API authentication.
   * @returns An observable of the HTTP POST response.
   */
  public initiateTotpSetup(
    accessToken: string
  ): Observable<IUserAuthenticationDetails> {
    const idToken = this._getIdTokenFromSession();
    const url = API_ENDPOINTS.AUTH_ENDPOINTS.newTotpSetup;
    const payload: { accessToken: string } = { accessToken };
    const customHeaders = new HttpHeaders().set(
      'Authorization',
      `Bearer ${idToken}`
    );

    return this._http.post(
      url,
      payload,
      {
        headers: customHeaders,
        attachTenantId: true,
      },
      ServiceType.AUTH
    );
  }

  /**
   * Verifies the TOTP code during MFA setup.
   * @param totpCode The TOTP code entered by the user.
   * @param accessToken Access token for API authentication.
   * @returns An observable of the HTTP POST response.
   */
  public verifyTotpCode(totpCode: number, accessToken: string) {
    const idToken = this._getIdTokenFromSession();
    const url = API_ENDPOINTS.AUTH_ENDPOINTS.newTotpVerifySetup;
    const payload: OtpVerificationPayload = { code: totpCode, accessToken };
    const customHeaders = new HttpHeaders().set(
      'Authorization',
      `Bearer ${idToken}`
    );

    return this._http.post(
      url,
      payload,
      {
        headers: customHeaders,
        attachTenantId: true,
      },
      ServiceType.AUTH
    );
  }

  /**
   * Verifies login using TOTP.
   * @param payload The TOTP verification payload.
   * @returns An observable of the HTTP POST response.
   */
  public verifyLoginWithTotp(
    payload: IMFAVerificationPayload
  ): Observable<IAuthResponse> {
    const url = API_ENDPOINTS.AUTH_ENDPOINTS.newTotpVerify;
    return this._http.post(
      url,
      payload,
      {
        attachTenantId: true,
      },
      ServiceType.AUTH
    );
  }

  /**
   * Verifies login using SMS-based MFA.
   * @param payload The SMS verification payload.
   * @returns An observable of the HTTP POST response.
   */
  public verifyLoginWithSms(
    payload: IMFAVerificationPayload
  ): Observable<IAuthResponse> {
    const url = API_ENDPOINTS.AUTH_ENDPOINTS.newSmsVerify;
    return this._http.post(
      url,
      payload,
      {
        attachTenantId: true,
      },
      ServiceType.AUTH
    );
  }

  /**
   * Retrieves the ID token from local storage.
   * @returns The ID token if available; otherwise, an empty string.
   */
  private _getIdTokenFromSession(): string {
    const sessionInfo = localStorage.getItem('tempSessionInfo');
    const parsedSession = sessionInfo ? JSON.parse(sessionInfo) : {};
    return parsedSession?.idToken || '';
  }
}
